// frontend/src/components/PasskeyPromptModal.js
// ログイン後に「パスキー (Face ID / Touch ID) を設定しませんか？」を自動提案するモーダル。
//
// 表示条件 (すべて満たすとき):
//   1. 認証済み (Supabase セッション有り)
//   2. WebAuthn 対応ブラウザ
//   3. そのユーザーがまだパスキー未登録 (user_passkeys 0 件)
//   4. そのユーザーが「今後表示しない」を選んでいない (localStorage.passkey_prompt_dismissed_<uid>)
//   5. 運営パネル・初回パスワード変更画面ではない
//   6. internal staff セッションでない (組織に成り代わり中は不要)
//
// 操作:
//   - 今すぐ設定  → /settings/passkey に遷移
//   - 後で       → セッションのみ閉じる (次回ログインでまた出る)
//   - 今後表示しないチェック + 閉じる  → localStorage に記録、以降出ない

import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Key, X, Shield } from "lucide-react";
import { supabase } from "../supabase";

const DISMISS_KEY_PREFIX = "passkey_prompt_dismissed_";
const SESSION_SHOWN_KEY = "passkey_prompt_shown_this_session";

export default function PasskeyPromptModal() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    // 現在のパス的に表示 NG な画面
    const path = location.pathname || "";
    if (path.startsWith("/change-password")) return;
    if (path.startsWith("/internal")) return;
    if (path.startsWith("/ops-") || path.includes("/login")) return;
    if (path.startsWith("/invite/")) return;
    if (path.startsWith("/settings/passkey")) return;

    // ブラウザが WebAuthn 非対応なら黙る
    if (typeof window === "undefined" || !window.PublicKeyCredential) return;

    // Internal モード中 (組織に成り代わり中) は不要
    if (localStorage.getItem("internal_selected_org")) return;

    // このセッションで既に 1 回出した → 連打しない
    if (sessionStorage.getItem(SESSION_SHOWN_KEY) === "1") return;

    let cancelled = false;
    const check = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return;
        const uid = session.user.id;
        if (cancelled) return;
        setUserId(uid);

        // 「今後表示しない」記憶チェック
        if (localStorage.getItem(DISMISS_KEY_PREFIX + uid) === "1") return;

        // 既にパスキー登録済みか
        const { count, error } = await supabase
          .from("user_passkeys")
          .select("id", { count: "exact", head: true });
        if (error) {
          console.warn("passkey prompt: user_passkeys 取得失敗", error.message);
          return;
        }
        if ((count || 0) > 0) return;

        if (cancelled) return;
        sessionStorage.setItem(SESSION_SHOWN_KEY, "1");
        setOpen(true);
      } catch (e) {
        console.warn("passkey prompt check error:", e?.message || e);
      }
    };
    // ちょっと遅らせてから (画面描画の邪魔をしない)
    const t = setTimeout(check, 1200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [location.pathname]);

  const close = () => {
    if (dontShowAgain && userId) {
      try { localStorage.setItem(DISMISS_KEY_PREFIX + userId, "1"); } catch (_) {}
    }
    setOpen(false);
  };

  const goSetup = () => {
    // 「今すぐ設定」でも dontShowAgain にチェックが入っていれば尊重する
    if (dontShowAgain && userId) {
      try { localStorage.setItem(DISMISS_KEY_PREFIX + userId, "1"); } catch (_) {}
    }
    setOpen(false);
    navigate("/settings/passkey");
  };

  if (!open) return null;

  return (
    <div style={styles.overlay} onClick={close}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button onClick={close} style={styles.closeBtn} title="閉じる" aria-label="閉じる">
          <X size={18} />
        </button>

        <div style={styles.iconWrap}>
          <div style={styles.iconBg}>
            <Shield size={28} color="#1d4ed8" />
          </div>
        </div>

        <h2 style={styles.title}>パスキーで安全・簡単ログイン</h2>
        <p style={styles.body}>
          Face ID / Touch ID / 指紋認証を使って、<br />
          パスワードなしでログインできるようになります。
        </p>

        <ul style={styles.benefits}>
          <li><Key size={14} style={{ color: "#1d4ed8" }} /> パスワードを打つ手間ゼロ</li>
          <li><Shield size={14} style={{ color: "#1d4ed8" }} /> フィッシング被害を根本的に防止</li>
        </ul>

        <label style={styles.checkRow}>
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          <span style={{ fontSize: 12, color: "#64748b" }}>今後表示しない</span>
        </label>

        <div style={styles.actions}>
          <button onClick={close} style={styles.secondaryBtn}>
            後で
          </button>
          <button onClick={goSetup} style={styles.primaryBtn}>
            <Key size={16} /> 今すぐ設定
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    padding: 16,
    animation: "fadeIn 0.2s ease-out",
  },
  modal: {
    background: "white",
    borderRadius: 16,
    padding: "28px 24px 20px",
    maxWidth: 420,
    width: "100%",
    position: "relative",
    boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
    textAlign: "center",
  },
  closeBtn: {
    position: "absolute",
    top: 12,
    right: 12,
    background: "transparent",
    border: "none",
    color: "#94a3b8",
    cursor: "pointer",
    padding: 6,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  iconWrap: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 12,
  },
  iconBg: {
    width: 60,
    height: 60,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: 18,
    fontWeight: 800,
    color: "#0f172a",
    margin: "0 0 10px",
  },
  body: {
    fontSize: 13,
    color: "#475569",
    lineHeight: 1.6,
    margin: "0 0 14px",
  },
  benefits: {
    listStyle: "none",
    padding: "10px 14px",
    margin: "0 0 14px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    textAlign: "left",
    fontSize: 12.5,
    color: "#334155",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  checkRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    userSelect: "none",
    margin: "0 0 14px",
  },
  actions: {
    display: "flex",
    gap: 8,
    justifyContent: "center",
  },
  primaryBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "10px 20px",
    background: "linear-gradient(135deg, #1d4ed8, #1e40af)",
    color: "white",
    border: "none",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(29, 78, 216, 0.3)",
  },
  secondaryBtn: {
    padding: "10px 20px",
    background: "white",
    color: "#475569",
    border: "1.5px solid #cbd5e1",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
};
